import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PlaneTakeoff } from "lucide-react";
import { useDispatch } from "react-redux";
import {
  authUserLogin,
  authUserRegister,
} from "../redux/reduxThunk/thunkAuthUser/AuthUser";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PhoneInputField from "./auth/PhoneInputField";
import { parsePhoneNumber, isValidPhoneNumber } from "react-phone-number-input";

// MODIFIED: Accept isAdminMode prop
const AuthForm = ({ isAdminMode = false, onUserAdded }) => {
  const { setUser, setEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // MODIFIED: In admin mode, always show registration form
  const [isLogin, setIsLogin] = useState(isAdminMode ? false : true);
  const [disableBtn, setDisableBtn] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [backendError, setBackendError] = useState("");

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: "",
    hometowncity: "",
    DOB: "",
    Aniversary: "",
    refrence: "",
    phone: "",
    role: "user", // MODIFIED: Added role field for admin panel
  });

  // MODIFIED: Show success message and callback - prevent duplicates with toastId
  const showToast = (message, type = "success", toastId = null) => {
    const id = toastId || `${type}-${Date.now()}`;
    
    // Dismiss any existing toast with the same ID first
    toast.dismiss(id);
    
    // Small delay to ensure dismissal completes
    setTimeout(() => {
      if (type === "success") {
        toast.success(message, {
          toastId: id, // Prevent duplicate toasts - same ID will replace existing
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          closeButton: true, // Ensure close button is visible
        });

        // If in admin mode and user added successfully, trigger callback
        if (isAdminMode && type === "success" && onUserAdded) {
          setTimeout(() => {
            onUserAdded();
          }, 1000);
        }
      } else if (type === "error") {
        toast.error(message, {
          toastId: id, // Prevent duplicate toasts - same ID will replace existing
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          closeButton: true, // Ensure close button is visible
        });
      }
    }, 50);
  };

  // Validation rules
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "username":
        if (!value.trim()) error = "Username is required";
        else if (value.length < 2)
          error = "Username must be at least 2 characters";
        break;

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) error = "Email is required";
        else if (!emailRegex.test(value))
          error = "Please enter a valid email address";
        break;

      case "password":
        if (!value) error = "Password is required";
        else if (value.length < 8)
          error = "Password must be at least 8 characters";
        else if (!/(?=.*[A-Za-z])(?=.*\d)/.test(value))
          error = "Password must contain letters and numbers";
        break;

      case "confirmPassword":
        if (!value) error = "Please confirm your password";
        else if (value !== formData.password) error = "Passwords do not match";
        break;

      case "gender":
        if (!value) error = "Gender is required";
        break;

      case "phone":
        if (!value || !value.trim() || /^\+\d{1,4}$/.test(value.trim())) {
          error = "Phone number is required";
        } else if (!isValidPhoneNumber(value)) {
          error = "Please enter a valid phone number";
        }
        break;

      case "hometowncity":
        if (!value.trim()) error = "City is required";
        break;

      case "DOB":
        if (!value) error = "Date of birth is required";
        else if (value) {
          const dob = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - dob.getFullYear();
          if (age < 13) error = "You must be at least 13 years old";
        }
        break;

      case "role":
        if (!value) error = "Role is required";
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    const fieldsToValidate = isLogin
      ? ["email", "password"]
      : [
          "username",
          "email",
          "password",
          "confirmPassword",
          "gender",
          "phone",
          "hometowncity",
          "DOB",
          "role", // Added role validation
        ];

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) newErrors[field] = error;
    });

    if (!isLogin) {
      if (
        formData.password !== formData.confirmPassword &&
        !newErrors.confirmPassword
      ) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, formData[name]);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handlePhoneChange = (val) => {
    const phoneVal = val || "";
    setFormData((prev) => ({ ...prev, phone: phoneVal }));

    if (backendError) {
      setBackendError("");
    }

    if (touched.phone) {
      const error = validateField("phone", phoneVal);
      setErrors((prev) => ({
        ...prev,
        phone: error,
      }));
    }
  };

  const handlePhoneBlur = () => {
    setTouched((prev) => ({ ...prev, phone: true }));
    const error = validateField("phone", formData.phone);
    setErrors((prev) => ({
      ...prev,
      phone: error,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (backendError) {
      setBackendError("");
    }

    if (name === "password" && touched.confirmPassword) {
      const confirmError = validateField(
        "confirmPassword",
        formData.confirmPassword
      );
      setErrors((prev) => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setBackendError("");

    // Create touched object for all relevant fields
    const touchedObj = {};
    const fieldsToTouch = isLogin
      ? ["email", "password"]
      : [
          "username",
          "email",
          "password",
          "confirmPassword",
          "gender",
          "phone",
          "hometowncity",
          "DOB",
          "role",
        ];

    fieldsToTouch.forEach((field) => {
      touchedObj[field] = true;
    });
    setTouched(touchedObj);

    if (!validateForm()) {
      if (formData.password !== formData.confirmPassword && !isLogin) {
        // showToast(
        //   "Passwords do not match. Please check and try again.",
        //   "error"
        // );
      } else {
        // showToast("Please fix the form errors before submitting", "error");
      }
      return;
    }

    // Additional password match check
    if (!isLogin && formData.password !== formData.confirmPassword) {
      showToast("Passwords do not match. Please check and try again.", "error");
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    }

    // MODIFIED: In admin mode, only handle registration
    if (!isLogin) {
      setDisableBtn(true);

      // Extract formatted phone number from E.164 value (e.g. +91 9876543210)
      let formattedPhone = formData.phone || "";
      if (formData.phone) {
        try {
          const parsed = parsePhoneNumber(formData.phone);
          if (parsed) {
            formattedPhone = `+${parsed.countryCallingCode} ${parsed.nationalNumber}`;
          }
        } catch {
          formattedPhone = formData.phone;
        }
      }

      const { confirmPassword, ...registerData } = formData;
      registerData.phone = formattedPhone;

      if (isAdminMode) {
        import("axios").then((axios) => {
          axios.default.post(`${import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000"}/api/auth/admin/register`, registerData, { withCredentials: true })
            .then((response) => {
              if (!response.data.success) {
                const errorMsg = response.data.message || "Registration failed. Please try again.";
                setBackendError(errorMsg);
                showToast(errorMsg, "error");
                return;
              }
              showToast("User added successfully! ✅", "success");
              // Reset form but keep role selection
              setFormData((prev) => ({
                ...prev,
                username: "",
                email: "",
                password: "",
                confirmPassword: "",
                gender: "",
                hometowncity: "",
                DOB: "",
                Aniversary: "",
                refrence: "",
                phone: "",
                role: "user", // Keep role as default
              }));
            })
            .catch((error) => {
              const errorMsg = error.response?.data?.message || "Registration failed. Please try again.";
              setBackendError(errorMsg);
              showToast(errorMsg, "error");
            })
            .finally(() => setDisableBtn(false));
        });
      } else {
        dispatch(authUserRegister(registerData))
          .then((res) => {
            if (!res?.payload?.success) {
              const errorMsg = res?.payload?.message || "Registration failed. Please try again.";
              setBackendError(errorMsg);
              showToast(errorMsg, "error");
              return;
            }
            showToast(res?.payload?.message || "Registration successful! ✅", "success");
            // Original behavior for regular registration
            setFormData((prev) => ({
              ...prev,
              username: "",
              email: "",
              password: "",
              confirmPassword: "",
              gender: "",
              hometowncity: "",
              DOB: "",
              Aniversary: "",
              refrence: "",
              city: "",
              phone: "",
              role: "user",
            }));
            setEmail(formData?.email);
            navigate("/email-vefication", {
              state: res?.payload?.data,
            });
          })
          .finally(() => setDisableBtn(false));
      }

      setErrors({});
      setTouched({});
      setBackendError("");

    } else {
      // Original login logic (not used in admin mode)
      setDisableBtn(true);
      dispatch(authUserLogin(formData))
        .then((res) => {
          console.log(res);
          if (res && res?.payload?.success) {
            showToast(
              res?.payload?.message || "Login successful! ✈️",
              "success",
              "login-success"
            );
            setUser(true);
            const redirectPath = location.state?.from || "/";
            setTimeout(() => {
              navigate(redirectPath, { replace: true });
            }, 1000);
          } else {
            console.log(res);

            const errorMsg = res?.payload?.message || "Login failed";
            setBackendError(errorMsg);
            showToast(errorMsg, "error", "login-error");
          }
        })
        .catch((error) => {
          // ... original login error handling
        })
        .finally(() => {
          setDisableBtn(false);
        });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referral = params.get("ref");

    if (referral) {
      setFormData((prev) => ({
        ...prev,
        refrence: referral,
      }));
    }
  }, [location.search]);

  // Helper function to render input
  const renderInput = (
    field,
    label,
    type = "text",
    placeholder = "",
    options = []
  ) => (
    <motion.div
      key={field}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
    >
      <label className="block text-gray-700 font-medium mb-2">
        {label}{" "}
        {!isLogin && field !== "refrence" && field !== "Aniversary" && (
          <span className="text-red-500">*</span>
        )}
      </label>
      {type === "select" ? (
        <select
          name={field}
          value={formData[field]}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-full px-4 py-3 border-2 rounded-lg outline-none transition-all ${
            errors[field] && touched[field]
              ? "border-red-500 focus:ring-2 focus:ring-red-400"
              : "border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
          }`}
          required={!isLogin && field !== "refrence" && field !== "Aniversary"}
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={field}
          value={formData[field]}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full px-4 py-3 border-2 rounded-lg outline-none transition-all ${
            errors[field] && touched[field]
              ? "border-red-500 focus:ring-2 focus:ring-red-400"
              : "border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
          }`}
          required={!isLogin && field !== "refrence" && field !== "Aniversary"}
          minLength={
            field === "password" || field === "confirmPassword" ? 8 : undefined
          }
          maxLength={field === "phone" ? 10 : undefined}
        />
      )}
      {errors[field] && touched[field] && (
        <p className="mt-1 text-sm text-red-600">{errors[field]}</p>
      )}
    </motion.div>
  );

  // MODIFIED: Don't show login/register toggle in admin mode
  const showToggle = !isAdminMode;

  return (
    <div
      className={
        isAdminMode
          ? ""
          : "min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-400 via-blue-500 to-indigo-600 p-4"
      }
    >
      <motion.div
        className={
          isAdminMode
            ? "bg-white p-6"
            : "bg-white/90 backdrop-blur-lg rounded-2xl shadow-2xl p-8 w-full max-w-md"
        }
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {!isAdminMode && (
          <div className="flex justify-center mb-6">
            <motion.div
              initial={{ rotate: -10 }}
              animate={{ rotate: 0 }}
              transition={{ duration: 0.8 }}
            >
              <PlaneTakeoff size={50} className="text-sky-600" />
            </motion.div>
          </div>
        )}

        <h2
          className={`text-center ${
            isAdminMode ? "text-2xl" : "text-3xl"
          } font-bold text-gray-800 mb-2`}
        >
          {isAdminMode
            ? "Add New User"
            : isLogin
            ? "Welcome Back ✈️"
            : "Join the Sky Club ☁️"}
        </h2>
        <p className="text-center text-gray-500 mb-6">
          {isAdminMode
            ? "Create a new user account"
            : isLogin
            ? "Login to continue booking your dream flight!"
            : "Create an account to start your journey."}
        </p>

        {/* Backend Error Display */}
        {backendError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-600 font-medium">
                  {backendError}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <AnimatePresence mode="wait">
            {!isLogin &&
              renderInput("username", "Full Name", "text", "Full Name")}
          </AnimatePresence>

          {renderInput("email", "Email", "email", "info@celestialtours.co.in")}

          {!isLogin && (
            <>
              {renderInput("gender", "Gender", "select", "", [
                { value: "M", label: "Male" },
                { value: "F", label: "Female" },
                { value: "O", label: "Other" },
              ])}

              {/* MODIFIED: Added Role Selection for Admin */}
              {isAdminMode && (
                <motion.div
                  key="role"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <label className="block text-gray-700 font-medium mb-2">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-4 py-3 border-2 rounded-lg outline-none transition-all ${
                      errors.role && touched.role
                        ? "border-red-500 focus:ring-2 focus:ring-red-400"
                        : "border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                    }`}
                    required
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  {errors.role && touched.role && (
                    <p className="mt-1 text-sm text-red-600">{errors.role}</p>
                  )}
                </motion.div>
              )}

              {/* Phone Input with Country Code Selector */}
              <motion.div
                key="phone"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <label className="block text-gray-700 font-medium mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <PhoneInputField
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  defaultCountry="IN"
                  placeholder="Enter phone number"
                  required={!isLogin}
                  hasError={!!(errors.phone && touched.phone)}
                />
                {errors.phone && touched.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                )}
              </motion.div>

              {renderInput("hometowncity", "City", "text", "Your City")}

              {renderInput("DOB", "Date of Birth", "date")}

              {renderInput("Aniversary", "Anniversary (optional)", "date")}

              <motion.div
                key="refrence"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <label className="block text-gray-700 font-medium mb-2">
                  Referral (optional)
                </label>
                <input
                  type="text"
                  name="refrence"
                  value={formData.refrence}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Referral code"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-sky-400 focus:border-sky-400 outline-none transition-all"
                />
              </motion.div>
            </>
          )}

          {renderInput("password", "Password", "password", "••••••••")}

          <AnimatePresence mode="wait">
            {!isLogin && (
              <motion.div
                key="confirmPassword"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <label className="block text-gray-700 font-medium mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="••••••••"
                  className={`w-full px-4 py-3 border-2 rounded-lg outline-none transition-all ${
                    errors.confirmPassword && touched.confirmPassword
                      ? "border-red-500 focus:ring-2 focus:ring-red-400"
                      : formData.password &&
                        formData.confirmPassword &&
                        formData.password === formData.confirmPassword
                      ? "border-green-500 focus:ring-2 focus:ring-green-400"
                      : "border-gray-200 focus:ring-2 focus:ring-sky-400 focus:border-sky-400"
                  }`}
                  required={!isLogin}
                  minLength={8}
                />
                {errors.confirmPassword && touched.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.confirmPassword}
                  </p>
                )}
                {formData.password &&
                  formData.confirmPassword &&
                  formData.password === formData.confirmPassword && (
                    <p className="mt-1 text-sm text-green-600">
                      ✓ Passwords match
                    </p>
                  )}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="submit"
            className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
              disableBtn
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-sky-600 text-white hover:bg-sky-700 active:bg-sky-800"
            }`}
            disabled={disableBtn}
          >
            {isAdminMode
              ? disableBtn
                ? "Creating User..."
                : "Create User"
              : isLogin
              ? disableBtn
                ? "Logging in..."
                : "Login"
              : disableBtn
              ? "Creating Account..."
              : "Create Account"}
          </button>
        </form>

        {/* MODIFIED: Only show toggle if not in admin mode */}
        {showToggle && (
          <div className="text-center mt-6">
            <p className="text-gray-600">
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setErrors({});
                  setTouched({});
                  setPhoneNumber("");
                  setSelectedCountry(countryCodes[0]);
                  setBackendError("");
                }}
                className="text-sky-600 font-semibold hover:text-sky-800 hover:underline transition-colors"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </button>
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AuthForm;
